import { JsonValue } from '@bufbuild/protobuf';
import { pb } from '@q6cyber/proto2es';
import {
  DialogService,
  ImgMovement,
  ImgMovementDisabled,
  TADirectTypeEnum,
  UiHelpersService,
  UiModule,
} from '@shared/ui';
import { FeatureFlagsService } from 'apps/phoenix/src/app/shared/services';
import { ExportHelpers } from 'apps/phoenix/src/app/shared/ui/services/export-helper/export-helper.service';
import { scrollToNthHighlight } from 'apps/phoenix/src/app/shared/ui/utilities/highlight.utils';
import { PhoenixTrpcClient } from 'apps/phoenix/src/app/utils';
import { Subject, from, takeUntil } from 'rxjs';

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  WritableSignal,
  effect,
  inject,
  signal,
} from '@angular/core';

import { TRPCClientError } from '@trpc/client';

import { DirectCollectService } from '../../services/direct-collect.service';
import { TimelineScreenshotMessageComponent } from '../timeline-screenshot-message/timeline-screenshot-message.component';

@Component({
  selector: 'phoenix-timeline-detail',
  imports: [UiModule, TimelineScreenshotMessageComponent],
  templateUrl: './timeline-detail.component.html',
  styles: [
    `
      ::ng-deep .highlight-scroll {
        outline: 2px solid orange;
        background-color: rgba(255, 255, 0, 0.4);
        transition: background-color 0.3s ease;
      }

      :host ::ng-deep em {
        font-family: Roboto, 'Helvetica Neue', sans-serif;
        background-color: rgba(255, 255, 0, 1);
        font-style: normal;
        color: black;
        font-weight: bold;
        padding-right: 2px;
      }
    `,
  ],
})
export class TimelineDetailComponent implements OnChanges, OnDestroy {
  destroy$ = new Subject<void>();
  formattedMessage: JsonValue = {};
  //The selected item data from BE
  itemGetResponse!: any;
  taDirectTypeEnum = TADirectTypeEnum;
  shouldShowHighlight: boolean = false;
  private currentHighlightIndex = 0;

  translateLabel = '';

  @ViewChild('dialogImgTemplate') dialogImgTemplate!: TemplateRef<unknown>;
  @ViewChild('imgActionsTemplate') imgActionsTemplate!: TemplateRef<unknown>;
  @ViewChild('highlightContainer')
  highlightContainerRef!: ElementRef<HTMLDivElement>;

  timelineService = inject(DirectCollectService);
  uiHelper = inject(UiHelpersService);
  renderer = inject(Renderer2);
  trpcService = inject(PhoenixTrpcClient);
  dialogService = inject(DialogService);
  featureFlagsService = inject(FeatureFlagsService);
  @Output() imgMovement = new EventEmitter<ImgMovement>();
  @Input() timelineSelectedItem: any;
  @Input() removeMovement: boolean = false;
  @Input() movementDisabled: ImgMovementDisabled = null;

  private readonly dataReponse: WritableSignal<any> = signal(null);

  constructor() {
    effect(() => {
      if (this.dataReponse()) {
        this.itemGetResponse = this.uiHelper.formatResponse(this.dataReponse());
        this.translateLabel = this.itemGetResponse?.textLanguage || 'Original';

        this.shouldShowHighlight =
          this.featureFlagsService.isOn('experiment_dcv_highlight') &&
          this.itemGetResponse?.highlight &&
          Object.values(this.itemGetResponse.highlight).some(
            value => Array.isArray(value) && value.length > 0
          );

        this.formattedMessage = this.itemGetResponse
          ? pb.collect.DirectCollect.fromJson(this.itemGetResponse, {
              ignoreUnknownFields: true,
            }).toJson({ emitDefaultValues: false, useProtoFieldName: true })
          : {};

        this.timelineService.itemLoading = false;
      } else {
        this.itemGetResponse = null;
        this.formattedMessage = {};
        this.translateLabel = '';
        this.shouldShowHighlight = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timelineSelectedItem']) {
      const currentValue = changes['timelineSelectedItem'].currentValue;
      if (currentValue && currentValue.id?.length > 0) {
        this.getItemData();
      } else {
        this.timelineSelectedItem = null;
      }
    }
  }

  getItemData() {
    if (!this.timelineSelectedItem?.id) return;

    this.timelineService.itemLoading = true;
    from(
      this.trpcService.directCollect.get.query({
        id: this.timelineSelectedItem.id,
        searchTerm: this.timelineService.usedFilters$()?.query ?? '',
      })
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.currentHighlightIndex = 0;
          this.dataReponse.set(data);
        },
        error: error => {
          this.dataReponse.set(null);
          this.timelineService.itemLoading = false;
          const message =
            error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND'
              ? 'Direct Collect not found'
              : 'Error loading Direct Collect!';
          this.uiHelper.handleErrorMessage(error, message);
        },
      });
  }

  imgError() {
    if (this.itemGetResponse?.imgproxy_urls) {
      this.itemGetResponse.imgproxy_urls.full = this.uiHelper.placeholderPath;
    }
  }

  downloadImg() {
    if (this.itemGetResponse?.imgproxy_urls?.download) {
      ExportHelpers.downloadLink(
        this.renderer,
        this.itemGetResponse.imgproxy_urls.download
      );
    }
  }

  hasTranslations(): boolean {
    return !!this.itemGetResponse?.translation;
  }

  get selectedItemData() {
    if (
      this.timelineService.showTranslations$() &&
      this.itemGetResponse?.translation
    ) {
      return this.itemGetResponse.translation;
    }
    return this.itemGetResponse
      ? this.timelineService.selectedItemData({
          ...this.itemGetResponse,
          type: this.timelineSelectedItem?.type,
        })
      : '';
  }

  openFullSizeImg(disableInteractions = true): void {
    this.dialogService.info(this.dialogImgTemplate, {
      panelClass: ['timeline-detail__empty-dialog-container'],
      width: '80%',
      data: {
        showActions: true,
        templateActions: this.imgActionsTemplate,
        context: { $implicit: { disableInteractions } },
      },
    });
  }

  scrollToFirstHighlight = () => {
    const container = this.highlightContainerRef?.nativeElement;
    if (!container) return;

    this.currentHighlightIndex = scrollToNthHighlight(
      container,
      this.currentHighlightIndex
    );
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
